import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Switch, Spin, Button } from "antd";
import { getCurrentUser } from "../apis";
import { useTranslation } from "react-i18next";
import { FcFolder } from "react-icons/fc";
import { FaArrowLeft } from "react-icons/fa";

// Styles are imported globally via styles/index.css

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userPermissions, setUserPermissions] = useState([]);
  const [allInOneServices, setAllInOneServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null); // null, 'rd', 'clients'
  const [isAdmin, setIsAdmin] = useState(false);
  const [submodulesOpen, setSubmodulesOpen] = useState(true);

  // Mapping between API service keys and sub-module IDs
  const serviceToModuleMap = {
    fcd_network: "fcd_network",
    simfix_areas: "sim_fix",
    predict: "predict",
    tl_green_wave: "tl_green_wave",
    signalfix: "signal_fix",
    od_analysis: "od_analysis",
    mystery_client: "mystery_client",
    semafix: "semafix",
    assign_x: "assign_x",
  };

  useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        const user = await getCurrentUser();
        setUserPermissions(user.permissions || []);
        setAllInOneServices(user.all_in_one_services || []);
        setIsAdmin(user.is_admin || false);
      } catch (error) {
        console.error("Errore nel recupero dei permessi utente:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserPermissions();
  }, []);

  const modules = [
    {
      id: "semafix",
      title: t("home.modules.semafix.title"),
      link: "/semafix",
      description: t("home.modules.semafix.description"),
      icon: require("../Assets/semafix.png"),
      category: "clients",
    },
    {
      id: "onde_verde",
      link: "/onde_verde",
      title: t("home.modules.syncfix.title"),
      description: t("home.modules.syncfix.description"),
      icon: require("../Assets/ondeverde.png"),
      category: "clients",
    },
    {
      id: "signal_fix",
      link: "/signal_fix",
      title: t("home.modules.signalfix.title"),
      description: t("home.modules.signalfix.description"),
      icon: require("../Assets/signalfix.png"),
      category: "clients",
    },
    {
      id: "mystery_client",
      link: "/mystery_client",
      title: t("home.modules.mysteryx.title"),
      description: t("home.modules.mysteryx.description"),
      icon: require("../Assets/mysteryclient.png"),
      category: "clients",
    },
    {
      id: "betwin",
      link: "/betwin",
      title: t("home.modules.betwin.title"),
      description: t("home.modules.betwin.description"),
      icon: require("../Assets/betwin.png"),
      category: "rd",
      adminOnly: true,
    },
    {
      id: "Lavagna",
      link: "/Lavagna",
      title: t("Lavagna"),
      description: t("Lavagna.description"),
      icon: require("../Assets/Lavagna.jpeg"),
      category: "rd",
      adminOnly: true,
    },
    {
      id: "traffix_all_in_one",
      link: "/traffix_all_in_one",
      title: t("Traffix"),
      description: t("Traffix All In One"),
      icon: require("../Assets/TraffixLogocolored.png"),
      category: "rd",
    },
    {
      id: "AIWayport",
      link: "/AIWayport",
      title: t("AIWayport"),
      description: t("AIWayport"),
      icon: require("../Assets/aiwayport.png"),
      category: "rd",
      adminOnly: true,
    },
    {
      id: "sim_fix",
      link: "/sim_fix/confronto",
      title: t("home.modules.simfix.title"),
      description: t("home.modules.simfix.description"),
      icon: require("../Assets/SimFix.png"),
      category: "clients",
    },
    {
      id: "analysis",
      link: "/analysis",
      title: t("Analysis"),
      description: t("analysis description"),
      icon: require("../Assets/analysis.png"),
      category: "rd",
      adminOnly: true,
    },
  ];

  const handleModuleClick = (moduleId, hasPermission) => {
    if (hasPermission) {
      navigate(`${moduleId}`);
    }
  };

  const folders = [
    {
      id: "rd",
      title: "R&D Projects",
      color: "#353A0A",
    },
    {
      id: "clients",
      title: "CLIENTS MODULES",
      color: "#353A0A",
    },
  ];

  // Admin-only modules are never shown to regular users
  // traffix_all_in_one is shown separately near the sub-modules section for non-admin users
  // Clients see NO grid cards — all their modules live inside Traffix All In One
  const filteredModules = currentFolder
    ? modules.filter((m) => m.category === currentFolder)
    : [];

  // Clients see empty grid — only Traffix All In One section is shown to them
  const displayModules = isAdmin ? filteredModules : [];

  // The standalone Traffix card rendered above the sub-modules (non-admin only)
  const traffixModule = modules.find((m) => m.id === "traffix_all_in_one");

  // ── Traffix sub-modules ──────────────────────────────────────────────────
  const traffixSubModules = [
    {
      id: "fcd_network",
      title: "FCD Network",
      tagline: "rete reale e dati traffico",
      description: "Rete stradale intelligente basata su Floating Car Data Costruisce una rappresentazione digitale georeferenziata della rete stradale alimentata da dati GPS e Floating Car Data. Il modulo rende interrogabili dati di traffico storici e live attraverso indicatori trasportistici avanzati, consentendo analisi comparative tra periodi, condizioni operative e configurazioni di rete. costituisce il layer informativo di base della piattaforma, fornendo una rete dinamica popolata da metriche di velocità, tempi di percorrenza e livelli di congestione a supporto delle successive analisi e valutazioni.",
      icon: require("../Assets/modules_icons/FCD_Network.svg").default,
      color: "#000000ff",
    },
    {
      id: "sim_fix",
      title: "SimFix",
      tagline: "confronto simulazioni",
      description: "Analisi comparativa e visualizzazione degli scenari simulati Visualizza e confronta i risultati delle microsimulazioni del traffico attraverso indicatori chiave quali accodamenti, livelli di servizio e metriche arco per arco. Il modulo consente di analizzare scenari progettuali alternativi e identificare la configurazione più performante sulla base delle prestazioni complessive della rete, facilitando la valutazione tecnica di interventi infrastrutturali o modifiche alla regolazione del traffico.",
      icon: require("../Assets/modules_icons/simfix_areas.svg").default,
      color: "#000000ff",
    },
    {
      id: "predict",
      title: "PredictX",
      tagline: "previsione traffico",
      description: "Previsione dei tempi di percorrenza tramite modelli di Machine Learning Modulo predittivo che stima l'evoluzione futura delle condizioni di traffico su link o percorsi selezionati con orizzonti temporali configurabili (15, 30, 45 o 60 minuti). Attraverso modelli di machine learning applicati alle serie temporali di traffico, PredictX anticipa variazioni di rete e condizioni di congestione, supportando attività operative, monitoraggio proattivo e valutazioni strategiche.",
      icon: require("../Assets/modules_icons/predict.svg").default,
      color: "#000000ff",
    },
    {
      id: "tl_green_wave",
      title: "SyncFix",
      tagline: "coordinamento e onde verdi",
      description: "Calcolo dell'onda verde e sincronizzazione semaforica automatizzata Calcola gli offset semaforici ottimali a partire dalla velocità di progressione desiderata lungo un corridoio urbano, consentendo la progettazione e la verifica di onde verdi tra impianti consecutivi. Il modulo permette di caricare, modificare e georeferenziare impianti semaforici esistenti o di progetto, simulando il coordinamento tramite animazioni dinamiche e diagrammi di fase. SyncFix supporta l'ottimizzazione della progressione veicolare e la riduzione delle fermate lungo gli assi principali di traffico.",
      icon: require("../Assets/modules_icons/TL_GREEN_WAVE.svg").default,
      color: "#000000ff",
    },
    {
      id: "signal_fix",
      title: "SignalFix",
      tagline: "censimento infrastrutture",
      description: "Censimento mobile e gestione digitale della segnaletica stradale Rileva e geolocalizza in movimento la segnaletica verticale mediante tecnologie di acquisizione mobile avanzate. I dati raccolti vengono automaticamente organizzati in un database georeferenziato aggiornabile ed editabile. Il modulo integra la ricostruzione tridimensionale delle aree rilevate, suddivise in unità territoriali (chunk), e strumenti di valutazione dello stato del manto stradale, costituendo un sistema gestionale completo degli elementi infrastrutturali censiti.",
      icon:require("../Assets/modules_icons/Signalfix.svg").default,
      color: "#000000ff",
    },
    {
      id: "od_analysis",
      title: "OD Analysis",
      tagline: "analisi domanda di mobilità",
      description: "Analisi delle matrici Origine–Destinazione su zonizzazioni personalizzate Consente l'analisi di matrici Origine–Destinazione derivate da dati di mobilità campionari su sistemi di zone configurabili dall'utente. Il modulo permette di definire zonizzazioni amministrative, funzionali o progettuali e analizzare gli scambi di traffico tra aree tramite indicatori sintetici e visualizzazioni dedicate, individuando pattern di mobilità, aree attrattive e principali corridoi di spostamento. Le matrici generate possono essere esportate per studi trasportistici e modelli simulativi.",
      icon: require("../Assets/modules_icons/OD_Analysis.svg").default,
      color: "#000000ff",
    },
    {
      id: "mystery_client",
      title: "MysteryX",
      tagline: "qualità TPL",
      description: "Valutazione della qualità del Trasporto Pubblico Locale. Consente il monitoraggio e la valutazione delle prestazioni del TPL attraverso indicatori di qualità del servizio, puntualità, frequenza e copertura territoriale. Il modulo integra dati operativi e di mobilità per individuare criticità nel servizio e supportare decisioni di ottimizzazione delle linee e degli orari, fornendo una visione complessiva dell'efficienza e dell'affidabilità del trasporto pubblico.",
      icon: require("../Assets/modules_icons/mystery_x.svg").default,
      color: "#000000ff",
    },
    {
      id: "semafix",
      title: "SemaFix",
      tagline: "progettazione semaforica",
      description: "Progettazione e ottimizzazione degli impianti semaforici. Supporta la progettazione, modifica e ottimizzazione dei piani semaforici per intersezioni singole o reti di impianti. Il modulo consente di definire fasi, tempi di verde, gialli e interverdi, verificare la conformità normativa e simulare gli effetti dei piani sull'efficienza della circolazione, riducendo attese e migliorando la sicurezza agli incroci.",
      icon: require("../Assets/modules_icons/semafix.svg").default,
      color: "#000000ff",
    },
    {
      id: "assign_x",
      title: "AssignX",
      tagline: "valutazione scenari rapidi",
      description: "Valutazione rapida della redistribuzione dei flussi veicolari. Modulo di assegnazione veicolare semplificata che consente di valutare rapidamente gli effetti di chiusure stradali, deviazioni o modifiche temporanee alla viabilità. Attraverso un modello di assegnazione light, il sistema stima in modo qualitativo la redistribuzione dei flussi sulle alternative di percorso, fornendo una rappresentazione visiva immediata degli impatti sulla rete. AssignX supporta analisi preliminari e decisioni operative rapide, configurandosi come strumento intermedio tra osservazione dei dati reali e simulazione avanzata.",
      icon: require("../Assets/modules_icons/assignx.svg").default,
      color: "#000000ff",
    },
  ];

  return (
    <div className="home-container">
      {/* Elementi di sfondo */}
      <div className="background-elements">
        <div className="circle circle-1"></div>
        <div className="circle circle-2"></div>
        <div className="circle circle-3"></div>
      </div>

      <div className="content-wrapper">
        <header className="header-section">
          <h1 className="main-title">{t("home.welcome")}</h1>
          <p className="subtitle">{t("home.subtitle")}</p>
        </header>

        {loading ? (
          <div className="home-loading-container">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {!isAdmin && traffixModule && (() => {
                const hasPermission = userPermissions.includes(traffixModule.id) || isAdmin;
                return (
                  <div className="traffix-combined-section">

                    {/* ── Mother card ── */}
                    <div
                      className={`traffix-mother-card ${!hasPermission ? "disabled-module" : ""}`}
                      onClick={() => handleModuleClick(traffixModule.link, hasPermission)}
                    >
                      {/* Decorative blobs */}
                      <div className="traffix-mother-blob traffix-mother-blob-1" />
                      <div className="traffix-mother-blob traffix-mother-blob-2" />

                     

                      {/* Top-right: switch */}
                      <div className="traffix-mother-right">
                        <Switch
                          checked={hasPermission}
                          disabled={!hasPermission}
                          className={hasPermission ? "permission-switch-enabled" : "permission-switch-disabled"}
                        />
                      </div>

                      {/* Icon (same as module-card) */}
                      <img
                        src={traffixModule.icon}
                        alt={traffixModule.title}
                        className="traffix-mother-logo"
                      />

                      {/* Center: title + subtitle + tags */}
                      <div className="traffix-mother-center">
                        <h2 className="traffix-mother-title">{traffixModule.title}</h2>
                        <p className="traffix-mother-sub">
                          Suite integrata · {traffixSubModules.length} moduli disponibili
                        </p>
                        <div className="traffix-mother-tags">
                          {traffixSubModules.slice(0, 5).map((s) => (
                            <span key={s.id} className="traffix-mother-tag">{s.title}</span>
                          ))}
                          <span className="traffix-mother-tag">+{traffixSubModules.length - 5}</span>
                        </div>
                      </div>

                      {/* Bottom: CTA */}
                      <div className="traffix-mother-cta">
                        <span>{t("home.explore")}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#353A0A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>

                    {/* Connector line / toggle bar */}
                    <div
                      className="traffix-connector"
                      onClick={(e) => { e.stopPropagation(); setSubmodulesOpen((o) => !o); }}
                      style={{ cursor: "pointer" }}
                      title={submodulesOpen ? "Nascondi moduli" : "Mostra moduli"}
                    >
                      <div className="traffix-connector-line" />
                      <span className="traffix-connector-label">Moduli inclusi</span>
                      <div className="traffix-connector-line" />
                      <svg
                        className={`traffix-connector-chevron${submodulesOpen ? " open" : ""}`}
                        width="16" height="16" viewBox="0 0 24 24" fill="none"
                      >
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>

                    {/* ── Sub-modules panel ── */}
                    <div className={`traffix-submodules-section${submodulesOpen ? "" : " submodules-collapsed"}`}>
                      <div className="traffix-submodules-header">
                        <img
                          src={require("../Assets/TraffixLogocolored.svg").default || require("../Assets/TraffixLogocolored.png")}
                          alt="Traffix"
                          className="traffix-submodules-logo"
                          onError={(e) => { e.target.src = require("../Assets/TraffixLogocolored.png"); }}
                        />
                        <div>
                          <h2 className="traffix-submodules-title">Traffix All In One</h2>
                          <p className="traffix-submodules-subtitle">Moduli inclusi nella piattaforma</p>
                        </div>
                      </div>
                      <div className="traffix-submodules-list">
                        {traffixSubModules.map((sub) => {
                          const serviceKey = Object.keys(serviceToModuleMap).find(
                            (key) => serviceToModuleMap[key] === sub.id
                          );
                          const hasAccess = serviceKey && allInOneServices.includes(serviceKey);
                          return (
                            <div
                              key={sub.id}
                              className="traffix-sub-row"
                            >
                              <div
                                className="traffix-sub-row-icon"
                              
                              >
                                <img
                                  src={sub.icon}
                                  alt={sub.title}
                                  style={{ width: "28px", height: "28px", objectFit: "contain" }}
                                />
                              </div>
                              <div className="traffix-sub-row-body">
                                <div className="traffix-sub-row-meta">
                                  <h4 className="traffix-sub-row-title" >{sub.title}</h4>
                                  {sub.tagline && <span className="traffix-sub-row-tagline">{sub.tagline}</span>}
                                </div>
                                <p className="traffix-sub-row-desc">{sub.description}</p>
                              </div>
                              <div className="traffix-sub-row-right">
                                <span
                                  className={`traffix-sub-status ${hasAccess ? "status-active" : "status-inactive"}`}
                                  title={hasAccess ? "Accesso attivo" : "Accesso non attivo"}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                  </div>
                );
              })()
            }


            <div className="modules-grid">
              {isAdmin && !currentFolder ? (
                // Admin Folder View
                folders.map((folder) => (
                  <div
                    key={folder.id}
                    className="module-card folder-card"
                    onClick={() => setCurrentFolder(folder.id)}
                    style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}
                  >
                    <div className="module-icon" style={{ fontSize: "8rem", display: "flex", justifyContent: "center", alignItems: "center", height: "auto", marginBottom: "1rem" }}>
                     <FcFolder />
                    </div>
                    <h3 className="module-title" style={{ color: folder.color }}>
                      {folder.title}
                    </h3>
                  </div>
                ))
              ) : (
                // Modules View (for regular users or admin inside a folder)
                <>
                  {isAdmin && currentFolder && (
                    <div style={{ width: "100%", gridColumn: "1 / -1", marginBottom: "1rem" }}>
                      <Button 
                        icon={<FaArrowLeft />} 
                        onClick={() => setCurrentFolder(null)}
                      >
                        Back to folders
                      </Button>
                    </div>
                  )}
                  {displayModules.map((module) => {
                    const hasPermission = userPermissions.includes(module.id) || isAdmin;
                    return (
                      <div
                        key={module.id}
                        className={`module-card ${module.id.replace("_", "-")} ${
                          !hasPermission ? "disabled-module" : ""
                        }`}
                        onClick={() => handleModuleClick(module.link, hasPermission)}
                      >
                        <div className="permission-switch">
                          <Switch
                            checked={hasPermission}
                            disabled={!hasPermission}
                            checkedChildren=""
                            unCheckedChildren=""
                            className={
                              hasPermission
                                ? "permission-switch-enabled"
                                : "permission-switch-disabled"
                            }
                          />
                        </div>
                        <div className="module-icon">
                          <img src={module.icon} alt={module.title} />
                        </div>
                        <h3 className="module-title">{module.title}</h3>
                        <p className="module-description">{module.description}</p>
                        <div className="module-action">
                          <span>{t("home.explore")}</span>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M5 12H19M19 12L12 5M19 12L12 19"
                              stroke="#A1E340"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Home;
